import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@proaxiv/ui'
import type { AppEvent } from '../../store/eventsSlice'
import { useLiveTick } from '../../hooks/useLiveTick'
import {
  mapsUrl,
  downloadIcs,
  isRegistrationOpen,
  isRegistrationHidden,
  loadGoogleFont,
  CURATED_FONTS,
  eventPathWithEmail,
} from '../../utils/event'
import { formatEventDateTime } from '../../utils/datetime'

// ── Icons ─────────────────────────────────────────────────────────────────────

const ExpandIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
)

const CloseIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Formats an event-schedule datetime in the event's own timezone (never the viewer's),
 *  using `locale` (the app's current UI language) for month/weekday names and date order.
 *  Omits the timezone suffix — DAJ feedback: public page shows wall-clock time only. */
function formatDateTime(iso: string, timezone: string | null | undefined, locale: string): string {
  return formatEventDateTime(
    iso,
    timezone,
    locale,
    {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    { includeTimeZoneName: false }
  )
}

/**
 * Returns true when registration is open, the deadline is the automatic (unoverridden)
 * mechanism, and that deadline is still in the future — the case where visitors benefit
 * from being told registration will close automatically (rather than only finding out
 * once it already has).
 */
function showsUpcomingAutoClosure(event: AppEvent): boolean {
  return (
    !event.registrationManualState &&
    !!event.registrationClosesAt &&
    Date.now() < Date.parse(event.registrationClosesAt)
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

/** Props for {@link PublicEventView}. */
export interface PublicEventViewProps {
  event: AppEvent
  /** When true, shows the "Draft mode" banner (org-member preview only). */
  draftPreview?: boolean
  /**
   * When true, renders inside the event editor's live preview pane rather than as the
   * real public page: navigational/destructive affordances (register link, calendar
   * download, full-size poster overlay) become inert so the preview stays safe and
   * doesn't distract the organizer from editing the form.
   */
  previewMode?: boolean
  /** Invitation email carried over from the public page's `?email=` query param, kept
   * prefilled through to the registration form. Ignored in `previewMode`. */
  inviteEmail?: string | null
}

/**
 * Presentational rendering of the public-facing event page. Shared by {@link PublicEventPage}
 * (real visitor-facing route, backed by store/API data) and the event editor's live preview
 * pane (backed by in-memory, unsaved form state) so organizers see a true WYSIWYG result.
 */
export function PublicEventView({
  event,
  draftPreview,
  previewMode,
  inviteEmail,
}: PublicEventViewProps) {
  const { t, i18n } = useTranslation()
  // Keeps the "Registration is closed" section accurate for a visitor who leaves this tab
  // open across the deadline, rather than only recomputing on the next unrelated re-render.
  useLiveTick()

  const regOpen = isRegistrationOpen(event)
  const bodyRef = useRef<HTMLDivElement>(null)
  const [posterOpen, setPosterOpen] = useState(false)

  // Close the poster overlay on Escape.
  useEffect(() => {
    if (!posterOpen) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setPosterOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [posterOpen])

  // Inject event fonts
  useEffect(() => {
    loadGoogleFont(event.branding.headingFont)
    loadGoogleFont(event.branding.bodyFont)
  }, [event.branding.headingFont, event.branding.bodyFont])

  // Staggered reveal on load — skipped in preview mode: the preview updates repeatedly as
  // the organizer edits, and a calm, motion-free preview is easier to read (COGA guidance
  // against unnecessary movement/animation).
  useEffect(() => {
    if (previewMode) return
    const items = bodyRef.current?.querySelectorAll<HTMLElement>('[data-reveal]')
    items?.forEach((el, i) => {
      el.style.opacity = '0'
      el.style.transform = 'translateY(28px)'
      el.style.transition = `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s`
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
        })
      })
    })
  }, [event, previewMode])

  const b = event.branding
  const headingFamily = b.headingFont
    ? `'${b.headingFont}', ${CURATED_FONTS.find((f) => f.family === b.headingFont)?.family ?? 'serif'}`
    : 'serif'
  const bodyFamily = b.bodyFont ? `'${b.bodyFont}', sans-serif` : 'sans-serif'

  // CSS variable injection for the event skin
  const cssVars = {
    '--ev-bg': b.background,
    '--ev-text': b.text,
    '--ev-primary': b.primary,
    '--ev-accent': b.accent,
    '--ev-heading-font': headingFamily,
    '--ev-body-font': bodyFamily,
  } as React.CSSProperties

  return (
    <div
      ref={bodyRef}
      style={{
        ...cssVars,
        background: 'var(--ev-bg)',
        color: 'var(--ev-text)',
        fontFamily: 'var(--ev-body-font)',
      }}
      className="relative min-h-screen"
    >
      {/* Atmospheric radial glow — spans the full page so the wash doesn't cut off at the header */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${b.primary}33 0%, transparent 70%), radial-gradient(ellipse 60% 40% at 80% 100%, ${b.accent}22 0%, transparent 60%)`,
        }}
        aria-hidden="true"
      />
      {/* ── Draft-mode banner (org-member preview only) ── */}
      {draftPreview && (
        <div
          role="alert"
          className="draft-preview-banner flex items-center gap-3 border-b border-orange-400/20 bg-orange-400/10 px-4 py-3 text-sm text-orange-300"
        >
          <span className="h-2 w-2 shrink-0 rounded-full bg-orange-400" aria-hidden="true" />
          <span>
            <strong className="font-semibold">{t('pep_draft_banner_title')}</strong> —{' '}
            {t('pep_draft_banner_body')}
          </span>
        </div>
      )}

      {/* ── Header ── */}
      <header className="relative px-4 pt-8 pb-10 sm:px-8 sm:pt-10 sm:pb-14">
        {/* ── Logo + Title — logo centered above the title ── */}
        <div data-reveal className="relative z-10 flex flex-col items-center gap-4">
          {/* Logo — centered above the title (omitted entirely when the event has none) */}
          {b.logo && (
            <img
              src={b.logo.dataUrl}
              alt={event.title}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="xs:max-h-14 xs:max-w-[160px] max-h-12 w-auto max-w-[140px] sm:max-h-[60px] sm:max-w-[180px] md:max-h-[72px] md:max-w-[220px] lg:max-h-[88px] lg:max-w-[260px] xl:max-h-[100px] xl:max-w-[300px]"
            />
          )}

          {/* Title — always centered below the logo */}
          <h1
            className="xs:text-3xl w-full text-center text-2xl leading-[1.05] font-black tracking-tight sm:text-4xl md:text-5xl"
            style={{ fontFamily: 'var(--ev-heading-font)', color: 'var(--ev-text)' }}
          >
            {event.title}
          </h1>
        </div>

        {/* Accent underline + meta — centered in viewport */}
        <div className="relative z-10 mx-auto max-w-3xl">
          <div
            data-reveal
            className="mx-auto mt-5 h-0.5 w-16 rounded-full"
            style={{ background: b.accent }}
          />

          {/* ── Meta: time · place · actions ── */}
          <div data-reveal className="mt-6 space-y-1 text-center">
            {/* Date / time — start only; end time and timezone suffix omitted per DAJ review */}
            {event.startDateTime && (
              <p className="text-xs font-medium break-words opacity-70 sm:text-sm">
                {formatDateTime(event.startDateTime, event.timezone, i18n.language)}
              </p>
            )}

            {/* Address — itself a Maps link so it has an accessible name matching the address */}
            {event.address &&
              (previewMode ? (
                <span
                  className="inline-flex items-start gap-1.5 text-xs font-semibold break-words sm:text-sm"
                  style={{ fontFamily: 'var(--ev-heading-font)', color: 'var(--ev-text)' }}
                >
                  <AddressPin color={b.accent} />
                  {event.address}
                </span>
              ) : (
                <a
                  href={mapsUrl(event.address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-start gap-1.5 text-xs font-semibold break-words transition-opacity hover:opacity-70 sm:text-sm"
                  style={{ fontFamily: 'var(--ev-heading-font)', color: 'var(--ev-text)' }}
                >
                  <AddressPin color={b.accent} />
                  {event.address}
                </a>
              ))}

            {/* Action buttons */}
            {(event.startDateTime || event.address) && (
              <div className="xs:flex-row xs:flex-wrap xs:items-center xs:justify-center flex flex-col items-stretch gap-2 pt-5">
                {event.startDateTime && (
                  <Button
                    type="button"
                    variant="unstyled"
                    onClick={() => {
                      if (!previewMode) downloadIcs(event)
                    }}
                    aria-disabled={previewMode || undefined}
                    className="xs:w-auto xs:justify-start flex w-full items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-80"
                    style={{
                      background: b.accent,
                      color: b.background,
                      fontFamily: 'var(--ev-body-font)',
                    }}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 3v12" />
                    </svg>
                    {t('pep_add_to_calendar')}
                  </Button>
                )}
                {event.address && (
                  // Unlike "Add to calendar" above (a no-op in preview — there's no real
                  // registration/ICS to generate for a draft), Maps just needs the address text
                  // the organiser already typed, so it's a real, working link in both preview and
                  // the live public page: opens Google Maps for that address in a new tab.
                  <a
                    href={mapsUrl(event.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="xs:w-auto xs:justify-start flex w-full items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-80"
                    style={{
                      background: `${b.primary}18`,
                      color: 'var(--ev-text)',
                      border: `1px solid ${b.primary}28`,
                      fontFamily: 'var(--ev-body-font)',
                    }}
                  >
                    <MapPinIcon />
                    {t('pep_open_maps')}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="mx-auto max-w-2xl space-y-10 px-4 pb-16 sm:space-y-14 sm:px-6 sm:pb-24">
        {/* ── Announcements ── */}
        {event.text && (
          <section data-reveal className="flex flex-col gap-3">
            <Label primary={b.primary}>{t('pep_announcements_label')}</Label>
            <div
              className="rounded-2xl p-5 text-left text-sm leading-relaxed whitespace-pre-wrap sm:p-6"
              style={{ background: `${b.primary}10`, border: `1px solid ${b.primary}20` }}
            >
              {event.text}
            </div>
          </section>
        )}

        {/* ── Poster / Flyer ── */}
        {event.poster && (
          <div data-reveal className={event.text ? '-mt-5 sm:-mt-7' : undefined}>
            <button
              type="button"
              onClick={() => {
                if (!previewMode) setPosterOpen(true)
              }}
              // In preview there's no lightbox to open, so this isn't announced/titled as an
              // enlarge action, and the "zoom in" cursor + "View full size" badge (which promise
              // that interaction) are hidden below rather than merely disabled — unlike the
              // Google Maps / registration CTAs, there's no equivalent "greyed out but still
              // there" affordance that makes sense for a zoom cursor or a floating badge.
              title={previewMode ? undefined : t('pep_poster_view_full')}
              aria-label={previewMode ? undefined : t('pep_poster_view_full')}
              className={`group relative block w-full overflow-hidden rounded-2xl ${previewMode ? 'cursor-default' : 'cursor-zoom-in'}`}
            >
              <img
                src={event.poster.dataUrl}
                alt={t('pep_poster_alt')}
                loading="lazy"
                decoding="async"
                className="w-full rounded-2xl object-contain transition-opacity group-hover:opacity-90"
                style={{ maxHeight: '80vh' }}
              />
              {!previewMode && (
                <span className="absolute right-3 bottom-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white opacity-90 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                  <ExpandIcon />
                  {t('pep_poster_view_full')}
                </span>
              )}
            </button>
          </div>
        )}

        {/* ── Registration ── */}
        {!isRegistrationHidden(event) && (
          <section data-reveal className="flex flex-col gap-4">
            <Label primary={b.primary}>{t('pep_registration_label')}</Label>
            {regOpen ? (
              <div className="flex flex-col gap-2">
                {previewMode ? (
                  <span
                    className="flex items-center justify-center gap-2.5 rounded-2xl px-5 py-3.5 text-sm font-bold tracking-wide sm:px-6 sm:py-4 sm:text-base"
                    style={{
                      background: b.accent,
                      color: b.background,
                      fontFamily: 'var(--ev-heading-font)',
                    }}
                  >
                    <RegisterIcon />
                    {t('pep_register_now')}
                  </span>
                ) : (
                  <Link
                    to={eventPathWithEmail(event.id, '/register', inviteEmail)}
                    className="flex items-center justify-center gap-2.5 rounded-2xl px-5 py-3.5 text-sm font-bold tracking-wide transition-opacity hover:opacity-85 sm:px-6 sm:py-4 sm:text-base"
                    style={{
                      background: b.accent,
                      color: b.background,
                      fontFamily: 'var(--ev-heading-font)',
                    }}
                  >
                    <RegisterIcon />
                    {t('pep_register_now')}
                  </Link>
                )}
                {/* Plain-language notice that registration will close automatically — shown
                    only for the unoverridden, still-open, dated case (see showsUpcomingAutoClosure). */}
                {showsUpcomingAutoClosure(event) && (
                  <p className="text-center text-xs opacity-60">
                    {t('pep_closes_on', {
                      date: formatDateTime(
                        event.registrationClosesAt!,
                        event.timezone,
                        i18n.language
                      ),
                    })}
                  </p>
                )}
              </div>
            ) : (
              <div
                className="flex flex-col items-center gap-3 rounded-2xl px-5 py-5 text-center sm:px-6 sm:py-6"
                style={{ background: `${b.primary}08`, border: `1px dashed ${b.primary}30` }}
              >
                <ClosedIcon color={b.primary} />
                <p
                  className="font-semibold opacity-60"
                  style={{ fontFamily: 'var(--ev-heading-font)' }}
                >
                  {t('pep_registration_closed')}
                </p>
                {event.registrationClosesAt && !event.registrationManualState && (
                  <p className="text-xs opacity-40">
                    {t('pep_closed_on', {
                      date: formatDateTime(
                        event.registrationClosesAt,
                        event.timezone,
                        i18n.language
                      ),
                    })}
                  </p>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {/* ── Poster full-size overlay ── */}
      {/* Rendered outside the space-y body wrapper so sibling-spacing utilities
          (margin-top/bottom injected by `space-y-*`) never affect this fixed,
          viewport-covering overlay. */}
      {posterOpen && event.poster && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setPosterOpen(false)}
        >
          <Button
            type="button"
            variant="unstyled"
            onClick={() => setPosterOpen(false)}
            title="Close"
            aria-label="Close"
            className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <CloseIcon />
          </Button>
          <img
            src={event.poster.dataUrl}
            alt="Event poster"
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full rounded-lg object-contain"
          />
        </div>
      )}
    </div>
  )
}

// ── Shared small pieces ───────────────────────────────────────────────────────

function Label({ children, primary }: { children: React.ReactNode; primary: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 opacity-20" style={{ background: primary }} />
      <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-50">{children}</p>
      <div className="h-px flex-1 opacity-20" style={{ background: primary }} />
    </div>
  )
}

function AddressPin({ color }: { color: string }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="mt-0.5 shrink-0"
      style={{ color }}
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function MapPinIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function RegisterIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

function ClosedIcon({ color }: { color: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ color, opacity: 0.5 }}
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}
